import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Layout,
  Input,
  List,
  Card,
  Row,
  Col,
  Table,
  Breadcrumb,
  Typography,
  Button,
  Dropdown,
  Space,
  message,
  Avatar,
  Popconfirm,
  Upload,
  Modal,
  Form,
  Tag,
  Progress,
  Spin,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileTextOutlined,
  FolderOutlined,
  EditOutlined,
  AppstoreOutlined,
  BarsOutlined,
  ReloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Radio } from 'antd';
import { useOutletContext } from 'react-router-dom';
import { Toast } from 'flowbite-react';
import api from '../services/api';
import { Customer } from '../services/customers';
import { Business, businessService } from '../services/businesses';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import dayjs from 'dayjs';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};
const { TextArea } = Input;

interface CustomerFile {
  id: number;
  customer_id: number;
  business_id?: number | null;
  name: string;
  path: string;
  type: string;
  mime_type: string;
  size: number;
  folder: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  formatted_size?: string;
}

interface Folder {
  folder: string;
  customer_id: number;
  business_id?: number | null;
  customer_name: string;
  file_count?: number;
  year?: number;
}

export const Files: React.FC = () => {
  const [entityType, setEntityType] = useState<'customer' | 'business'>('customer');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [folderView, setFolderView] = useState<'grid' | 'list'>('grid');
  const [fileView, setFileView] = useState<'grid' | 'list'>('grid');
  const [files, setFiles] = useState<CustomerFile[]>([]);
  const [rootFiles, setRootFiles] = useState<CustomerFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [rootFilesLoading, setRootFilesLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [folderSearchText, setFolderSearchText] = useState('');
  const [fileSearchText, setFileSearchText] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [createFolderModalVisible, setCreateFolderModalVisible] = useState(false);
  const [createFolderLoading, setCreateFolderLoading] = useState(false);
  const [editFolderModalVisible, setEditFolderModalVisible] = useState(false);
  const [editFolderLoading, setEditFolderLoading] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm] = Form.useForm();
  const [createFolderForm] = Form.useForm();
  const [editFolderForm] = Form.useForm();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<Array<{ file: File; id: string }>>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const [viewingFileId, setViewingFileId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [personalCount, setPersonalCount] = useState(0);
  const [businessCount, setBusinessCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canView = hasPermission(user, 'files.view');
  const canCreate = hasPermission(user, 'files.create');
  const canUpdate = hasPermission(user, 'files.update');
  const canDelete = hasPermission(user, 'files.delete');
  const uploadQueueRef = useRef<Array<{ file: File; id: string }>>([]);
  const uploadStatusRef = useRef<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const isProcessingRef = useRef(false);
  const toastTimeoutRef = useRef<number | null>(null);
  const businessCustomerCache = useRef<Record<number, number | null>>({});
  const businessCustomerPromises = useRef<Record<number, Promise<number | null> | null>>({});
  const CONCURRENT_UPLOADS = 5; // 同时上传的文件数量
  const GRID_CARD_HEIGHT = 140;

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Files
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  useEffect(() => {
    if (!canView) {
      setCustomers([]);
      setBusinesses([]);
      setFolders([]);
      setFiles([]);
      setPersonalCount(0);
      setBusinessCount(0);
      return;
    }
    if (entityType === 'customer') {
    loadCustomers();
    } else {
      loadBusinesses();
    }
  }, [entityType, canView]);

  useEffect(() => {
    if (!canView) {
      setPersonalCount(0);
      setBusinessCount(0);
      return;
    }
    loadCounts();
  }, [canView]);

  useEffect(() => {
    // Reset selection when switching entity type
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    setSelectedFolder(null);
    setFolders([]);
    setFiles([]);
    setFolderSearchText('');
    setFileSearchText('');
  }, [entityType]);

  useEffect(() => {
    if (entityType === 'customer' && selectedCustomer) {
      loadFolders(selectedCustomer.id, null);
      loadRootFiles(selectedCustomer.id, null);
      setSelectedFolder(null);
      setFiles([]);
    } else if (entityType === 'business' && selectedBusiness) {
      (async () => {
        const customerId = await resolveBusinessCustomerId();
        loadFolders(customerId ?? null, selectedBusiness.id);
        loadRootFiles(customerId ?? null, selectedBusiness.id);
        setSelectedFolder(null);
        setFiles([]);
      })();
    } else {
      setFolders([]);
      setRootFiles([]);
      setSelectedFolder(null);
      setFiles([]);
    }
  }, [selectedCustomer, selectedBusiness, entityType]);

  useEffect(() => {
    if (selectedFolder) {
      const businessId = selectedFolder.business_id ?? (entityType === 'business' && selectedBusiness ? selectedBusiness.id : null);
      loadFiles(selectedFolder.customer_id ?? null, selectedFolder.folder, businessId);
      setRootFiles([]);
    } else {
      setFiles([]);
      if (entityType === 'customer' && selectedCustomer) {
        loadRootFiles(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadRootFiles(selectedBusiness.customer_id ?? null, selectedBusiness.id);
      } else {
        setRootFiles([]);
      }
    }
    setFileSearchText('');
  }, [selectedFolder, entityType, selectedBusiness, selectedCustomer]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const ROOT_FOLDER = '__root__';
  const normalizeFolderPath = (value: string) =>
    value
      .replace(/\\/g, '/')
      .replace(/\/+/g, '/')
      .replace(/^\/|\/$/g, '')
      .trim();

  const buildFolderPath = (parentPath: string | null, name: string) => {
    const normalizedName = normalizeFolderPath(name);
    if (!normalizedName) {
      return '';
    }
    if (!parentPath) {
      return normalizedName;
    }
    return `${parentPath}/${normalizedName}`;
  };

  const getFolderLabel = (path: string) => {
    const parts = normalizeFolderPath(path).split('/').filter(Boolean);
    return parts[parts.length - 1] || path;
  };

  const getParentFolderPath = (path: string) => {
    const parts = normalizeFolderPath(path).split('/').filter(Boolean);
    parts.pop();
    return parts.join('/');
  };

  const isDirectChildFolder = (path: string, parentPath: string) => {
    const normalizedPath = normalizeFolderPath(path);
    const normalizedParent = normalizeFolderPath(parentPath);
    if (!normalizedParent) {
      return normalizedPath !== '' && !normalizedPath.includes('/');
    }
    if (!normalizedPath.startsWith(`${normalizedParent}/`)) {
      return false;
    }
    const remainder = normalizedPath.slice(normalizedParent.length + 1);
    return remainder !== '' && !remainder.includes('/');
  };

  const showToast = useCallback((type: 'success' | 'error' | 'info', toastMessage: string) => {
    setToast({ type, message: toastMessage });
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 3000);
  }, []);

  const loadCustomers = async () => {
    if (!canView) {
      setCustomers([]);
      return;
    }
    setCustomersLoading(true);
    try {
      const response = await api.get('/files/customers');
      const data = response.data.data || [];
      setCustomers(data.filter((customer: any) => customer.enabled !== -1));
    } catch (error: any) {
      message.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  };

  const loadBusinesses = async () => {
    if (!canView) {
      setBusinesses([]);
      return;
    }
    setBusinessesLoading(true);
    try {
      const response = await api.get('/files/businesses');
      const data = response.data.data || [];
      setBusinesses(data.filter((business: any) => business.enabled !== -1));
    } catch (error: any) {
      message.error('Failed to load businesses');
    } finally {
      setBusinessesLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const [customersResponse, businessesResponse] = await Promise.all([
        api.get('/files/customers'),
        api.get('/files/businesses'),
      ]);
      const customersData = customersResponse.data.data || [];
      const businessesData = businessesResponse.data.data || [];
      setPersonalCount(customersData.filter((customer: any) => customer.enabled !== -1).length);
      setBusinessCount(businessesData.filter((business: any) => business.enabled !== -1).length);
    } catch (error: any) {
      message.error('Failed to load counts');
    }
  };

  const resolveBusinessCustomerId = async () => {
    if (!selectedBusiness) return null;
    if (selectedBusiness.customer_id) {
      return selectedBusiness.customer_id;
    }
    if (businessCustomerCache.current[selectedBusiness.id] !== undefined) {
      return businessCustomerCache.current[selectedBusiness.id];
    }
    if (businessCustomerPromises.current[selectedBusiness.id]) {
      return businessCustomerPromises.current[selectedBusiness.id]!;
    }
    businessCustomerPromises.current[selectedBusiness.id] = (async () => {
      try {
        const business = await businessService.getById(selectedBusiness.id);
        const customerId = business.customer_id ?? null;
        businessCustomerCache.current[selectedBusiness.id] = customerId;
        if (customerId && !selectedBusiness.customer_id) {
          setSelectedBusiness(prev => (prev?.id === business.id ? { ...prev, customer_id: customerId } : business));
        }
        if (!customerId) {
          message.error('Business has no linked customer');
        }
        return customerId;
      } catch {
        message.error('Failed to load business customer');
        return null;
      } finally {
        businessCustomerPromises.current[selectedBusiness.id] = null;
      }
    })();

    return businessCustomerPromises.current[selectedBusiness.id]!;
  };

  const loadFolders = async (customerId: number | null, businessId: number | null = null) => {
    if (!canView) {
      setFolders([]);
      return;
    }
    setFoldersLoading(true);
    try {
      let url = customerId ? `/files/folders?customer_id=${customerId}` : '/files/folders';
      if (businessId) {
        url += customerId ? `&business_id=${businessId}` : `?business_id=${businessId}`;
      }
      const response = await api.get(url);
      setFolders(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load folders');
    } finally {
      setFoldersLoading(false);
    }
  };

  const loadRootFiles = async (customerId: number | null, businessId: number | null = null) => {
    if (!canView) {
      setRootFiles([]);
      return;
    }
    if (!customerId && !businessId) {
      setRootFiles([]);
      return;
    }
    setRootFilesLoading(true);
    try {
      let url = `/files?folder=${encodeURIComponent(ROOT_FOLDER)}`;
      if (customerId) {
        url += `&customer_id=${customerId}`;
      }
      if (businessId) {
        url += `&business_id=${businessId}`;
      }
      const response = await api.get(url);
      setRootFiles(response.data.data || []);
    } catch {
      message.error('Failed to load root files');
    } finally {
      setRootFilesLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadCounts();
      if (selectedFolder) {
        const businessId = entityType === 'business' && selectedBusiness ? selectedBusiness.id : null;
        try {
          await api.post('/files/sync', {
            customer_id: selectedFolder.customer_id,
            business_id: businessId,
            folder: selectedFolder.folder,
          });
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to sync files');
        }
        await loadFiles(selectedFolder.customer_id ?? null, selectedFolder.folder, businessId);
        return;
      }
      if (entityType === 'customer' && selectedCustomer) {
        try {
          await api.post('/files/sync', { customer_id: selectedCustomer.id });
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to sync files');
        }
        await loadFolders(selectedCustomer.id, null);
        await loadRootFiles(selectedCustomer.id, null);
        return;
      }
      if (entityType === 'business' && selectedBusiness) {
        try {
          await api.post('/files/sync', {
            customer_id: selectedBusiness.customer_id ?? null,
            business_id: selectedBusiness.id,
          });
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to sync files');
        }
        await loadFolders(selectedBusiness.customer_id ?? null, selectedBusiness.id);
        await loadRootFiles(selectedBusiness.customer_id ?? null, selectedBusiness.id);
        return;
      }
      if (entityType === 'customer') {
        await loadCustomers();
        return;
      }
      await loadBusinesses();
    } finally {
      setRefreshing(false);
    }
  };

  const createMenuItems = [
    {
      key: 'folder',
      label: 'Folder',
      icon: <FolderOutlined />,
      onClick: () => setCreateFolderModalVisible(true),
    },
    {
      key: 'file',
      label: 'File',
      icon: <UploadOutlined />,
      onClick: () => setUploadModalVisible(true),
    },
  ];

  const loadFiles = async (customerId: number | null, folder: string, businessId: number | null = null) => {
    if (!canView) {
      setFiles([]);
      return;
    }
    setLoading(true);
    try {
      let url = `/files?folder=${encodeURIComponent(folder)}`;
      if (customerId) {
        url += `&customer_id=${customerId}`;
      }
      if (businessId) {
        url += `&business_id=${businessId}`;
      }
      const response = await api.get(url);
      setFiles(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (value: string) => {
    setSearchText(value);
    if (entityType === 'customer') {
    setCustomersLoading(true);
    try {
      const response = await api.get(`/files/customers?search=${encodeURIComponent(value)}`);
      setCustomers(response.data.data || []);
    } catch (error: any) {
      message.error('Failed to search customers');
    } finally {
      setCustomersLoading(false);
      }
    } else {
      setBusinessesLoading(true);
      try {
        const response = await api.get(`/files/businesses?search=${encodeURIComponent(value)}`);
        setBusinesses(response.data.data || []);
      } catch (error: any) {
        message.error('Failed to search businesses');
      } finally {
        setBusinessesLoading(false);
      }
    }
  };

  const selectFolderByPath = (folderPath: string | null) => {
    if (!folderPath) {
      setSelectedFolder(null);
      return;
    }
    const match = folders.find((folder) => folder.folder === folderPath);
    if (match) {
      setSelectedFolder(match);
    }
  };

  const handleView = async (file: CustomerFile) => {
    try {
      setViewingFileId(file.id);
      // Use axios to fetch file with authentication token
      const response = await api.get(`/files/${file.id}/view`, {
        responseType: 'blob',
      });
      
      // Create blob URL and open in new window
      const blob = new Blob([response.data], { type: response.headers['content-type'] || file.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      showToast('error', 'Failed to view file');
      message.error('Failed to view file');
    } finally {
      setViewingFileId(null);
    }
  };

  const handleDownload = async (file: CustomerFile) => {
    try {
      // Use axios to fetch file with authentication token
      const response = await api.get(`/files/${file.id}/download`, {
        responseType: 'blob',
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] || file.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download file');
    }
  };

  const handleDelete = async (file: CustomerFile) => {
    if (!canDelete) {
      message.warning('You do not have permission to delete files');
      return;
    }
    try {
      await api.delete(`/files/${file.id}`);
      message.success('File deleted successfully');
      if (selectedFolder) {
        const businessId = entityType === 'business' && selectedBusiness ? selectedBusiness.id : null;
        loadFiles(selectedFolder.customer_id ?? null, selectedFolder.folder, businessId);
      } else if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
        loadRootFiles(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
        loadRootFiles(selectedBusiness.customer_id ?? null, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error('Failed to delete file');
    }
  };

  const handleCreateFolder = async (values: any) => {
    if (!canCreate) {
      message.warning('You do not have permission to create folders');
      return;
    }
    try {
      console.log('[Files][CreateFolder] submit', {
        entityType,
        selectedCustomerId: selectedCustomer?.id,
        selectedBusinessId: selectedBusiness?.id,
        folder: values?.folder,
      });
      if (entityType === 'customer' && !selectedCustomer) {
        message.error('Please select a customer first');
        return;
      }
      if (entityType === 'business' && !selectedBusiness) {
        message.error('Please select a business first');
        return;
      }
      
      setCreateFolderLoading(true);
      const businessId = entityType === 'business' ? selectedBusiness!.id : null;
      const customerId =
        entityType === 'customer'
          ? selectedCustomer!.id
          : selectedBusiness?.customer_id ?? null;
      console.log('[Files][CreateFolder] resolved customer', {
        entityType,
        customerId,
        businessId,
      });
      if (entityType === 'customer' && !customerId) {
        message.error('Please select a valid customer');
        return;
      }
      
      const parentFolder = selectedFolder ? selectedFolder.folder : '';
      const resolvedFolder = buildFolderPath(parentFolder, values.folder);
      if (!resolvedFolder) {
        message.error('Please enter a valid folder name');
        return;
      }
      console.log('[Files][CreateFolder] request', {
        customer_id: customerId,
        business_id: businessId,
        folder: resolvedFolder,
      });
      const payload: { customer_id?: number; business_id?: number | null; folder: string } = {
        folder: resolvedFolder,
      };
      if (customerId) {
        payload.customer_id = customerId;
      }
      if (businessId) {
        payload.business_id = businessId;
      }
      const response = await api.post('/files/folders', payload);
      console.log('[Files][CreateFolder] response', response?.status, response?.data);

      showToast('success', 'Folder created successfully');
      message.success('Folder created successfully');
      setCreateFolderModalVisible(false);
      createFolderForm.resetFields();
      const resolvedCustomerId = response?.data?.data?.customer_id ?? customerId ?? null;
      if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(resolvedCustomerId, selectedBusiness.id);
      }
    } catch (error: any) {
      console.error('[Files][CreateFolder] error', error?.response?.status, error?.response?.data || error);
      const errorMessage = error.response?.data?.message || 'Failed to create folder';
      showToast('error', errorMessage);
      message.error(errorMessage);
    } finally {
      setCreateFolderLoading(false);
    }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    if (!canDelete) {
      message.warning('You do not have permission to delete folders');
      return;
    }
    try {
      if (entityType === 'customer' && !selectedCustomer) {
        message.error('Please select a customer first');
        return;
      }
      if (entityType === 'business' && !selectedBusiness) {
        message.error('Please select a business first');
        return;
      }
      
      const businessId = entityType === 'business' && selectedBusiness ? selectedBusiness.id : null;
      
      await api.delete('/files/folders', {
        data: {
          customer_id: folder.customer_id,
          business_id: businessId,
          folder: folder.folder,
        },
      });

      message.success('Folder deleted successfully');
      if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete folder');
    }
  };

  const handleEditFolder = async (values: any) => {
    if (!editingFolder) return;
    try {
      const parentPath = getParentFolderPath(editingFolder.folder);
      const resolvedFolder = buildFolderPath(parentPath || null, values.folder);
      if (!resolvedFolder) {
        message.error('Please enter a valid folder name');
        return;
      }
      setEditFolderLoading(true);
      await api.put('/files/folders', {
        customer_id: editingFolder.customer_id,
        business_id: editingFolder.business_id ?? null,
        folder: editingFolder.folder,
        new_folder: resolvedFolder,
      });

      message.success('Folder updated successfully');
      setEditFolderModalVisible(false);
      setEditingFolder(null);
      editFolderForm.resetFields();
      if (entityType === 'customer' && selectedCustomer) {
        loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update folder');
    } finally {
      setEditFolderLoading(false);
    }
  };

  // 批量上传单个文件
  const uploadSingleFile = async (file: File, fileId: string) => {
    try {
      if (!canCreate) {
        throw new Error('You do not have permission to upload files');
      }
      if (entityType === 'customer' && !selectedCustomer) {
        throw new Error('Please select a customer first');
      }
      if (entityType === 'business' && !selectedBusiness) {
        throw new Error('Please select a business first');
      }
      if (!selectedFolder) {
        throw new Error('Please select a folder first');
      }

      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [fileId]: 'uploading' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });

      let businessId: number | null = null;
      if (selectedFolder?.business_id !== undefined && selectedFolder.business_id !== null) {
        businessId = selectedFolder.business_id;
      } else if (entityType === 'business' && selectedBusiness) {
        businessId = selectedBusiness.id;
      }

      const customerId = entityType === 'customer' ? selectedCustomer!.id : await resolveBusinessCustomerId();
      if (!customerId) {
        throw new Error('Please select a valid business customer');
      }

      const formData = new FormData();
      formData.append('customer_id', customerId.toString());
      if (businessId) {
        formData.append('business_id', businessId.toString());
      }
      formData.append('file', file);
      formData.append('folder', selectedFolder.folder);

      await api.post('/files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [fileId]: percent }));
          }
        },
      });

      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [fileId]: 'success' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
    } catch (error: any) {
      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [fileId]: 'error' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });
      console.error(`Failed to upload ${file.name}:`, error);
    }
  };

  // 处理上传队列
  const processUploadQueue = useCallback(async () => {
    if (isProcessingRef.current) {
      return;
    }

    const checkAndProcess = async () => {
      if (uploadQueueRef.current.length === 0) {
        isProcessingRef.current = false;
        return;
      }

      // 获取待上传的文件（只处理 pending 状态的）
      const pendingUploads = uploadQueueRef.current
        .filter(item => {
          const status = uploadStatusRef.current[item.id] || uploadStatus[item.id];
          return status === 'pending';
        })
        .slice(0, CONCURRENT_UPLOADS);

      if (pendingUploads.length === 0) {
        // 检查是否所有文件都已完成
        const allCompleted = uploadQueueRef.current.every(item => {
          const status = uploadStatusRef.current[item.id] || uploadStatus[item.id];
          return status === 'success' || status === 'error';
        });

        if (allCompleted && uploadQueueRef.current.length > 0) {
          // 所有文件上传完成，刷新列表
          if (selectedFolder) {
            const businessId = selectedFolder.business_id ?? 
              (entityType === 'business' && selectedBusiness ? selectedBusiness.id : null);
            await loadFiles(selectedFolder.customer_id ?? null, selectedFolder.folder, businessId);
          }
          
          // 清理已完成的上传
          const currentStatus = uploadStatusRef.current;
          const successCount = Object.values(currentStatus).filter(s => s === 'success').length;
          const errorCount = Object.values(currentStatus).filter(s => s === 'error').length;
          
          if (successCount > 0) {
            message.success(`${successCount} file(s) uploaded successfully`);
          }
          if (errorCount > 0) {
            message.error(`${errorCount} file(s) failed to upload`);
          }

          setUploadQueue([]);
          uploadQueueRef.current = [];
          setUploadProgress({});
          setUploadStatus({});
          uploadStatusRef.current = {};
          isProcessingRef.current = false;
          return;
        }

        // 还有正在上传的文件，等待一下再检查
        setTimeout(() => {
          if (!isProcessingRef.current) {
            checkAndProcess();
          }
        }, 500);
        return;
      }

      // 启动新的上传
      const uploadPromises = pendingUploads.map(item => uploadSingleFile(item.file, item.id));
      await Promise.allSettled(uploadPromises);

      // 继续处理剩余的文件
      setTimeout(() => {
        checkAndProcess();
      }, 100);
    };

    isProcessingRef.current = true;
    checkAndProcess();
  }, [selectedFolder, selectedCustomer, selectedBusiness, entityType]);

  // 当上传队列变化时处理队列
  useEffect(() => {
    if (uploadQueue.length > 0 && !isProcessingRef.current) {
      processUploadQueue();
    }
  }, [uploadQueue.length]); // 只依赖队列长度，避免重复触发

  // 处理拖拽文件
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedFolder && canCreate) {
      setIsDragging(true);
    }
  }, [selectedFolder, canCreate]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!canCreate) {
      message.warning('You do not have permission to upload files');
      return;
    }
    if (!selectedFolder) {
      message.warning('Please select a folder first');
      return;
    }

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    const newFiles = droppedFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random()}-${file.name}`,
    }));

    setUploadQueue(prev => [...prev, ...newFiles]);
    uploadQueueRef.current = [...uploadQueueRef.current, ...newFiles];
    
    newFiles.forEach(item => {
      setUploadStatus(prev => {
        const newStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'> = { ...prev, [item.id]: 'pending' };
        uploadStatusRef.current = newStatus;
        return newStatus;
      });
      setUploadProgress(prev => ({ ...prev, [item.id]: 0 }));
    });

    message.info(`Added ${droppedFiles.length} file(s) to upload queue`);
  }, [selectedFolder, canCreate]);

  const handleUpload = async (values: any) => {
    try {
      if (!canCreate) {
        message.warning('You do not have permission to upload files');
        return;
      }
      if (entityType === 'customer' && !selectedCustomer) {
        message.error('Please select a customer first');
        return;
      }
      if (entityType === 'business' && !selectedBusiness) {
        message.error('Please select a business first');
        return;
      }
      
      setUploading(true);
      
      // Use business_id from selectedFolder if available, otherwise determine from entityType
      let businessId: number | null = null;
      if (selectedFolder?.business_id !== undefined && selectedFolder.business_id !== null) {
        businessId = selectedFolder.business_id;
      } else if (entityType === 'business' && selectedBusiness) {
        businessId = selectedBusiness.id;
      }
      
      const customerId = entityType === 'customer' ? selectedCustomer!.id : await resolveBusinessCustomerId();
      if (!customerId) {
        message.error('Please select a valid business customer');
        return;
      }
      
      const formData = new FormData();
      formData.append('customer_id', customerId.toString());
      if (businessId) {
        formData.append('business_id', businessId.toString());
      }
      formData.append('file', values.file.file);
      // Use selected folder or new folder name
      const folderName = values.folder || selectedFolder?.folder || 'files';
      formData.append('folder', folderName);

      await api.post('/files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showToast('success', 'File uploaded successfully');
      message.success('File uploaded successfully');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      
      // Refresh the file list
      if (selectedFolder) {
        // Use business_id from selectedFolder if available
        const folderBusinessId = selectedFolder.business_id ?? businessId;
        await loadFiles(selectedFolder.customer_id ?? null, selectedFolder.folder, folderBusinessId);
      } else if (entityType === 'customer' && selectedCustomer) {
        await loadFolders(selectedCustomer.id, null);
      } else if (entityType === 'business' && selectedBusiness) {
        await loadFolders(selectedBusiness.customer_id, selectedBusiness.id);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload file';
      showToast('error', errorMessage);
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (type: string) => {
    const iconStyle = { fontSize: 48, color: '#ff4d4f' };
    return <FileTextOutlined style={iconStyle} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const formatUploadDate = (value?: string | null): string => {
    if (!value) return '-';
    const dateValue = dayjs(value);
    if (!dateValue.isValid()) return '-';
    return dateValue.format('DD/MM/YYYY');
  };

  const getCustomerInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getStatusTag = (status?: string) => {
    if (!status) return null;
    const normalized = status.toLowerCase();
    const color = normalized === 'active' ? 'green' : normalized === 'inactive' ? 'red' : 'orange';
    const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    return <Tag color={color}>{label}</Tag>;
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const filteredBusinesses = businesses.filter((business) =>
    business.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const rootFolders = folders.filter((folder) => isDirectChildFolder(folder.folder, ''));
  const filteredFolders = rootFolders.filter((folder) =>
    getFolderLabel(folder.folder).toLowerCase().includes(folderSearchText.toLowerCase())
  );
  const childFolders = selectedFolder
    ? folders.filter((folder) => isDirectChildFolder(folder.folder, selectedFolder.folder))
    : [];
  const sortedChildFolders = [...childFolders].sort((a, b) =>
    getFolderLabel(a.folder).localeCompare(getFolderLabel(b.folder))
  );
  const breadcrumbSegments = selectedFolder
    ? normalizeFolderPath(selectedFolder.folder).split('/').filter(Boolean)
    : [];
  const breadcrumbPaths = breadcrumbSegments.map((_, index) =>
    breadcrumbSegments.slice(0, index + 1).join('/')
  );
  const filteredFiles = files.filter((file) =>
    file.name.toLowerCase().includes(fileSearchText.toLowerCase())
  );
  const filteredRootFiles = rootFiles.filter((file) =>
    file.name.toLowerCase().includes(folderSearchText.toLowerCase())
  );
  const folderRows = sortedChildFolders.map((folder) => ({
    id: `folder-${folder.customer_id}-${folder.folder}`,
    rowType: 'folder' as const,
    name: getFolderLabel(folder.folder),
    folder: folder.folder,
    customer_id: folder.customer_id,
    business_id: folder.business_id ?? null,
  }));
  const fileTableRows = fileView === 'list'
    ? [...folderRows, ...filteredFiles.map((file) => ({ ...file, rowType: 'file' as const }))]
    : [];
  const rootFileRows = filteredRootFiles.map((file) => ({ ...file, rowType: 'file' as const }));
  const folderListingRows = [...filteredFolders.map((folder) => ({
    id: `folder-${folder.customer_id}-${folder.folder}`,
    rowType: 'folder' as const,
    name: getFolderLabel(folder.folder),
    folder: folder.folder,
    customer_id: folder.customer_id,
    business_id: folder.business_id ?? null,
  })), ...rootFileRows];
  const toastAccent = toast?.type === 'success'
    ? 'bg-green-500'
    : toast?.type === 'error'
      ? 'bg-red-500'
      : 'bg-blue-500';

  const folderColumns = [
    {
      title: 'Name',
      dataIndex: 'folder',
      key: 'folder',
      render: (_: string, folder: Folder) => (
        <span style={{ color: '#1890ff', cursor: 'pointer' }}>{getFolderLabel(folder.folder)}</span>
      ),
    },
    {
      title: 'Files',
      dataIndex: 'file_count',
      key: 'file_count',
      render: (value: number | undefined) => value ?? 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, folder: Folder) => (
        <Space>
          {canUpdate && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                setEditingFolder(folder);
                editFolderForm.setFieldsValue({
                  folder: getFolderLabel(folder.folder),
                });
                setEditFolderModalVisible(true);
              }}
            />
          )}
          {canDelete && (
            <Popconfirm
              title="Are you sure you want to delete this folder?"
              description="This will delete all files in this folder. This action cannot be undone."
              onConfirm={(event) => {
                event?.stopPropagation();
                handleDeleteFolder(folder);
              }}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                onClick={(event) => event.stopPropagation()}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const fileColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_: string, record: any) => {
        if (record.rowType === 'folder') {
          return (
            <span
              style={{ color: '#1890ff', cursor: 'pointer' }}
              onClick={() => selectFolderByPath(record.folder)}
            >
              <FolderOutlined style={{ marginRight: 8 }} />
              {record.name}
            </span>
          );
        }
        return (
          <Tooltip title={record.name}>
            <Text style={{ display: 'inline-block', maxWidth: 260 }} ellipsis>
              {record.name}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Folder',
      dataIndex: 'folder',
      key: 'folder',
      render: (_: string | null, record: any) => {
        if (record.rowType === 'folder') {
          return getParentFolderPath(record.folder) || 'Root';
        }
        return record.folder || '-';
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_: string, record: any) => {
        if (record.rowType === 'folder') {
          return 'FOLDER';
        }
        return record.type?.toUpperCase() || '-';
      },
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (_: number, record: any) => {
        if (record.rowType === 'folder') {
          return '-';
        }
        return formatFileSize(record.size);
      },
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_: string, record: any) => {
        if (record.rowType === 'folder') {
          return '-';
        }
        return formatUploadDate(record.created_at);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: any) => {
        if (record.rowType === 'folder') {
          return (
            <Button
              type="primary"
              icon={<FolderOutlined />}
              onClick={() => selectFolderByPath(record.folder)}
              size="small"
            >
              Open
            </Button>
          );
        }
        return (
          <Space>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
              loading={viewingFileId === record.id}
            >
              View
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              size="small"
            />
            {canDelete && (
              <Popconfirm
                title="Are you sure you want to delete this file?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Layout style={{ height: 'calc(100vh - 112px)', background: '#fff', overflow: 'hidden', display: 'flex', margin: '-24px' }}>
      <Sider
        width={300}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          paddingRight: '16px',
          paddingTop: 0,
          paddingLeft: 0,
          paddingBottom: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <div style={{ padding: '16px 16px 0 16px', flexShrink: 0 }}>
        <Radio.Group
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          style={{ marginBottom: 16, width: '100%' }}
        >
          <Radio.Button value="customer" style={{ width: '50%', textAlign: 'center' }}>
            Personal
          </Radio.Button>
          <Radio.Button value="business" style={{ width: '50%', textAlign: 'center' }}>
            Business
          </Radio.Button>
        </Radio.Group>
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              flex: 1,
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              padding: '6px 8px',
              textAlign: 'center',
              background: '#fafafa',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Personal
            </Text>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{personalCount}</div>
          </div>
          <div
            style={{
              flex: 1,
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              padding: '6px 8px',
              textAlign: 'center',
              background: '#fafafa',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Business
            </Text>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{businessCount}</div>
          </div>
        </div>
        <Input
          placeholder={`Search ${entityType === 'customer' ? 'Personal' : 'Business'}`}
          prefix={<SearchOutlined />}
          style={{ marginBottom: 16 }}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px', minHeight: 0, maxHeight: '70%' }}>
        {entityType === 'customer' ? (
        <List
          loading={customersLoading}
          dataSource={filteredCustomers}
            pagination={false}
            style={{ minHeight: 0 }}
          renderItem={(customer) => (
            <List.Item
              style={{
                cursor: 'pointer',
                padding: '12px',
                borderRadius: 4,
                backgroundColor:
                  selectedCustomer?.id === customer.id ? '#e6f7ff' : 'transparent',
                marginBottom: 8,
              }}
              onClick={() => setSelectedCustomer(customer)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{
                      backgroundColor: '#1890ff',
                      color: '#fff',
                    }}
                  >
                    {getCustomerInitials(customer.name)}
                  </Avatar>
                }
                title={customer.name}
              />
            </List.Item>
          )}
        />
        ) : (
          <List
            loading={businessesLoading}
            dataSource={filteredBusinesses}
              pagination={false}
              style={{ minHeight: 0 }}
            renderItem={(business) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: 4,
                  backgroundColor:
                    selectedBusiness?.id === business.id ? '#e6f7ff' : 'transparent',
                  marginBottom: 8,
                }}
                onClick={() => setSelectedBusiness(business)}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: '#52c41a',
                        color: '#fff',
                      }}
                    >
                      {getCustomerInitials(business.name)}
                    </Avatar>
                  }
                  title={business.name}
                />
              </List.Item>
            )}
          />
        )}
        </div>
      </Sider>
      <Content 
        style={{ 
          flex: 1,
          overflow: 'hidden',
          background: '#fff',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          border: isDragging && selectedFolder ? '2px dashed #1890ff' : '2px dashed transparent',
          borderRadius: isDragging && selectedFolder ? 8 : 0,
          backgroundColor: isDragging && selectedFolder ? '#e6f7ff' : '#fff',
          transition: 'all 0.3s',
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {toast && (
          <div className="fixed right-6 top-6 z-50">
            <Toast>
              <div className={`mr-3 h-2 w-2 rounded-full ${toastAccent}`} />
              <div className="text-sm font-normal">{toast.message}</div>
              <button
                type="button"
                onClick={() => setToast(null)}
                className="ml-3 inline-flex h-5 w-5 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                aria-label="Close"
              >
                x
              </button>
            </Toast>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {isDragging && selectedFolder && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <UploadOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
            <div style={{ fontSize: 18, color: '#1890ff', fontWeight: 500 }}>
              Drop files here to upload
            </div>
          </div>
        )}
        {selectedFolder ? (
          // Third level: Files view
          <>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Title level={4} style={{ margin: 0 }}>{selectedFolder.customer_name}</Title>
                  {entityType === 'customer'
                    ? getStatusTag(selectedCustomer?.type)
                    : getStatusTag(selectedBusiness?.type)}
                </div>
                <Breadcrumb style={{ marginTop: 6 }}>
                  <Breadcrumb.Item>
                    <span
                      style={{ color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedFolder(null);
                        setFiles([]);
                      }}
                    >
                      Folders
                    </span>
                  </Breadcrumb.Item>
                  {breadcrumbPaths.map((path) => (
                    <Breadcrumb.Item key={path}>
                      <span
                        style={{ color: '#1890ff', cursor: 'pointer' }}
                        onClick={() => selectFolderByPath(path)}
                      >
                        {getFolderLabel(path)}
                      </span>
                    </Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              </div>
              <Space>
                <Input
                  placeholder="Search files"
                  prefix={<SearchOutlined />}
                  value={fileSearchText}
                  onChange={(e) => setFileSearchText(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={refreshing}>
                  Refresh
                </Button>
                <Radio.Group
                  value={fileView}
                  onChange={(e) => setFileView(e.target.value)}
                >
                  <Radio.Button value="grid">
                    <AppstoreOutlined />
                  </Radio.Button>
                  <Radio.Button value="list">
                    <BarsOutlined />
                  </Radio.Button>
                </Radio.Group>
                {canCreate && (
                  <Dropdown menu={{ items: createMenuItems }} trigger={['click']}>
                    <Button type="primary" icon={<PlusOutlined />}>
                      Create
                    </Button>
                  </Dropdown>
                )}
              </Space>
            </div>
            {/* Upload Progress */}
            {uploadQueue.length > 0 && (
              <Card style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Uploading {uploadQueue.length} file(s)</Text>
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {uploadQueue.map((item) => {
                    const status = uploadStatus[item.id] || 'pending';
                    const progress = uploadProgress[item.id] || 0;
                    return (
                      <div key={item.id} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text style={{ fontSize: '12px' }} ellipsis>
                            {item.file.name}
                          </Text>
                          <Tag
                            color={
                              status === 'success' ? 'green' :
                              status === 'error' ? 'red' :
                              status === 'uploading' ? 'blue' : 'default'
                            }
                          >
                            {status === 'success' ? 'Done' :
                             status === 'error' ? 'Failed' :
                             status === 'uploading' ? 'Uploading' : 'Pending'}
                          </Tag>
                        </div>
                        {status === 'uploading' && (
                          <Progress percent={progress} size="small" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
            <Spin
              spinning={loading || viewingFileId !== null}
              tip={viewingFileId ? 'Loading file...' : 'Loading files...'}
            >
              {fileView === 'grid' ? (
                <Row gutter={[16, 16]}>
                  {sortedChildFolders.map((folder) => (
                    <Col key={`${folder.customer_id}-${folder.folder}`} xs={24} sm={12} md={8} lg={8}>
                      <Card
                        hoverable
                        onClick={() => selectFolderByPath(folder.folder)}
                        style={{
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          height: GRID_CARD_HEIGHT,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        bodyStyle={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '16px',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 10,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {canUpdate && (
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              size="small"
                              onClick={() => {
                                setEditingFolder(folder);
                                editFolderForm.setFieldsValue({
                                  folder: getFolderLabel(folder.folder),
                                });
                                setEditFolderModalVisible(true);
                              }}
                            />
                          )}
                          {canDelete && (
                            <Popconfirm
                              title="Are you sure you want to delete this folder?"
                              description="This will delete all files in this folder. This action cannot be undone."
                              onConfirm={() => handleDeleteFolder(folder)}
                              okText="Yes"
                              cancelText="No"
                              okButtonProps={{ danger: true }}
                            >
                              <Button danger type="text" icon={<DeleteOutlined />} size="small" />
                            </Popconfirm>
                          )}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <FolderOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                        </div>
                        <Title level={5} style={{ marginBottom: 0, marginTop: 0 }}>
                          {getFolderLabel(folder.folder)}
                        </Title>
                      </Card>
                    </Col>
                  ))}
                  {filteredFiles.map((file) => (
                    <Col key={file.id} xs={24} sm={12} md={8} lg={8}>
                      <Card
                        style={{
                          height: GRID_CARD_HEIGHT,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
                      >
                        <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                          <div style={{ textAlign: 'center', minWidth: 60 }}>
                            {getFileIcon(file.type)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                              <Tooltip title={file.name}>
                                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px', maxWidth: '100%' }} ellipsis>
                                  {file.name}
                                </Text>
                              </Tooltip>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {file.type.toUpperCase()} • {formatFileSize(file.size)}
                              </Text>
                              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                                Uploaded: {formatUploadDate(file.created_at)}
                              </Text>
                            </div>
                            <Space style={{ marginTop: 12 }}>
                              <Button
                                type="primary"
                                icon={<EyeOutlined />}
                                onClick={() => handleView(file)}
                                size="small"
                                loading={viewingFileId === file.id}
                              >
                                View
                              </Button>
                              <Button
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(file)}
                                size="small"
                              />
                              {canDelete && (
                                <Popconfirm
                                  title="Are you sure you want to delete this file?"
                                  onConfirm={() => handleDelete(file)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    size="small"
                                  />
                                </Popconfirm>
                              )}
                            </Space>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Table
                  dataSource={fileTableRows}
                  columns={fileColumns}
                  rowKey={(record: any) => record.id}
                  pagination={false}
                  size="small"
                />
              )}
              {sortedChildFolders.length === 0 && filteredFiles.length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No files found
                </div>
              )}
            </Spin>
          </>
        ) : (entityType === 'customer' && selectedCustomer) || (entityType === 'business' && selectedBusiness) ? (
          // Second level: Folders view (cards)
          <>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Title level={3} style={{ margin: 0 }}>
                  {entityType === 'customer' ? selectedCustomer?.name : selectedBusiness?.name}
                </Title>
                {entityType === 'customer'
                  ? getStatusTag(selectedCustomer?.type)
                  : getStatusTag(selectedBusiness?.type)}
              </div>
              <Space>
                <Input
                  placeholder="Search folders"
                  prefix={<SearchOutlined />}
                  value={folderSearchText}
                  onChange={(e) => setFolderSearchText(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={refreshing}>
                  Refresh
                </Button>
                <Radio.Group
                  value={folderView}
                  onChange={(e) => setFolderView(e.target.value)}
                >
                  <Radio.Button value="grid">
                    <AppstoreOutlined />
                  </Radio.Button>
                  <Radio.Button value="list">
                    <BarsOutlined />
                  </Radio.Button>
                </Radio.Group>
                {canCreate && (
                  <Dropdown menu={{ items: createMenuItems }} trigger={['click']}>
                    <Button type="primary" icon={<PlusOutlined />}>
                      Create
                    </Button>
                  </Dropdown>
                )}
              </Space>
            </div>
            <Spin spinning={foldersLoading || rootFilesLoading}>
              {folderView === 'grid' ? (
                <Row gutter={[16, 16]}>
                  {filteredFolders.map((folder) => {
                    const color = '#1890ff';
                    return (
                      <Col key={`${folder.customer_id}-${folder.folder}`} xs={24} sm={12} md={8} lg={8}>
                        <Card
                          hoverable
                          onClick={() => selectFolderByPath(folder.folder)}
                          style={{
                            borderRadius: 8,
                            textAlign: 'center',
                            cursor: 'pointer',
                            position: 'relative',
                          }}
                          bodyStyle={{ padding: '24px' }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 10,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {canUpdate && (
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => {
                                  setEditingFolder(folder);
                                  editFolderForm.setFieldsValue({
                                    folder: getFolderLabel(folder.folder),
                                  });
                                  setEditFolderModalVisible(true);
                                }}
                              />
                            )}
                            {canDelete && (
                              <Popconfirm
                                title="Are you sure you want to delete this folder?"
                                description="This will delete all files in this folder. This action cannot be undone."
                                onConfirm={() => handleDeleteFolder(folder)}
                                okText="Yes"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                              >
                                <Button
                                  danger
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  style={{
                                    color: '#ff4d4f',
                                  }}
                                />
                              </Popconfirm>
                            )}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <FolderOutlined
                              style={{
                                fontSize: 64,
                                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                color: color,
                              }}
                            />
                          </div>
                          <Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
                            {getFolderLabel(folder.folder)}
                          </Title>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            <FileTextOutlined />
                            <Text type="secondary">
                              {folder.file_count || 0} {folder.file_count === 1 ? 'file' : 'files'}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                  {filteredRootFiles.map((file) => (
                    <Col key={file.id} xs={24} sm={12} md={8} lg={8}>
                      <Card
                        style={{
                          height: GRID_CARD_HEIGHT,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
                      >
                        <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                          <div style={{ textAlign: 'center', minWidth: 60 }}>
                            {getFileIcon(file.type)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                              <Tooltip title={file.name}>
                                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px', maxWidth: '100%' }} ellipsis>
                                  {file.name}
                                </Text>
                              </Tooltip>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {file.type.toUpperCase()} • {formatFileSize(file.size)}
                              </Text>
                              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                                Uploaded: {formatUploadDate(file.created_at)}
                              </Text>
                            </div>
                            <Space style={{ marginTop: 12 }}>
                              <Button
                                type="primary"
                                icon={<EyeOutlined />}
                                onClick={() => handleView(file)}
                                size="small"
                                loading={viewingFileId === file.id}
                              >
                                View
                              </Button>
                              <Button
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(file)}
                                size="small"
                              />
                              {canDelete && (
                                <Popconfirm
                                  title="Are you sure you want to delete this file?"
                                  onConfirm={() => handleDelete(file)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button danger icon={<DeleteOutlined />} size="small" />
                                </Popconfirm>
                              )}
                            </Space>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Table
                  dataSource={folderListingRows}
                  columns={fileColumns}
                  rowKey={(record: any) => record.id}
                  pagination={false}
                  size="small"
                  onRow={(record: any) => ({
                    onClick: () => {
                      if (record.rowType === 'folder') {
                        selectFolderByPath(record.folder);
                      }
                    },
                  })}
                />
              )}
              {filteredFolders.length === 0 && filteredRootFiles.length === 0 && !foldersLoading && !rootFilesLoading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No files found for this customer
                </div>
              )}
            </Spin>
          </>
        ) : (
          // First level: No selection
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {customers.length === 0 ? (
              <div>No customers found</div>
            ) : (
              <div>Select a customer to view folders</div>
            )}
          </div>
        )}
        </div>

        <Modal
          title="Create Folder"
          open={createFolderModalVisible}
          onCancel={() => {
            setCreateFolderModalVisible(false);
            createFolderForm.resetFields();
          }}
          footer={null}
        >
          <Spin spinning={createFolderLoading}>
            <Form
              form={createFolderForm}
              layout="vertical"
              onFinish={handleCreateFolder}
            >
              <Form.Item
                name="folder"
                label="Folder Name"
                help={selectedFolder ? `Parent: ${selectedFolder.folder}` : undefined}
                rules={[{ required: true, message: 'Please enter folder name' }]}
              >
                <Input placeholder="Enter folder name" />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={createFolderLoading}>
                    Create
                  </Button>
                  <Button
                    onClick={() => {
                      setCreateFolderModalVisible(false);
                      createFolderForm.resetFields();
                    }}
                    disabled={createFolderLoading}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title="Edit Folder"
          open={editFolderModalVisible}
          onCancel={() => {
            setEditFolderModalVisible(false);
            setEditingFolder(null);
            editFolderForm.resetFields();
          }}
          footer={null}
        >
          <Spin spinning={editFolderLoading}>
            <Form
              form={editFolderForm}
              layout="vertical"
              onFinish={handleEditFolder}
            >
              <Form.Item
                name="folder"
                label="Folder Name"
                help={editingFolder ? `Parent: ${getParentFolderPath(editingFolder.folder) || 'Root'}` : undefined}
                rules={[{ required: true, message: 'Please enter folder name' }]}
              >
                <Input placeholder="Enter folder name" />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={editFolderLoading} disabled={editFolderLoading}>
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      setEditFolderModalVisible(false);
                      setEditingFolder(null);
                      editFolderForm.resetFields();
                    }}
                    disabled={editFolderLoading}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title="Upload File"
          open={uploadModalVisible}
          onCancel={() => {
            if (!uploading) {
            setUploadModalVisible(false);
            uploadForm.resetFields();
              setUploading(false);
            }
          }}
          footer={null}
          closable={!uploading}
          maskClosable={!uploading}
        >
          <Spin spinning={uploading}>
            <Form
              form={uploadForm}
              layout="vertical"
              onFinish={handleUpload}
            >
              <Form.Item
                name="file"
                label="File"
                rules={[{ required: true, message: 'Please select a file' }]}
              >
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                name="folder"
                label="Folder"
                initialValue={selectedFolder?.folder || ''}
              >
                <Input placeholder="Folder name" disabled={!!selectedFolder} />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={uploading} disabled={uploading}>
                    Upload
                  </Button>
                  <Button
                    onClick={() => {
                      setUploadModalVisible(false);
                      uploadForm.resetFields();
                    }}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Spin>
        </Modal>
      </Content>
    </Layout>
  );
};
